"use client";

import { useAuth } from "@clerk/nextjs";
import { Languages } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { JLPTBadge } from "~/components/videos/jlpt-badge";
import {
  JLPT_LEVELS,
  JLPT_LEVELS_DESCRIPTIONS,
} from "~/server/db/videos/constants";
import { useShowKnownLemmas } from "~/state/videos/hooks/use-show-known-lemmas";

export default function LexemesFilterControl({
  videoId,
  jlptLanguageLevel: initialJLPTLanguageLevel,
}: {
  videoId: number;
  jlptLanguageLevel: number;
}) {
  const { isSignedIn } = useAuth();
  const [jlptLanguageLevel, setJLPTLanguageLevel] = useState(
    initialJLPTLanguageLevel || 0,
  );
  const router = useRouter();

  const { isShowKnownLemmasEnabled, toggleShowKnownLemmas } =
    useShowKnownLemmas();

  const handleJLPTLevelChange = (level: number) => {
    router.push(
      ["/videos", videoId, level || undefined].filter(Boolean).join("/"),
      { scroll: true },
    );
    setJLPTLanguageLevel(level);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          {jlptLanguageLevel ? (
            <JLPTBadge level={jlptLanguageLevel} />
          ) : (
            <Languages size={17} />
          )}
          <span className="sr-only">Filter</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Filter Words</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={jlptLanguageLevel.toString()}
          onValueChange={(value) => handleJLPTLevelChange(parseInt(value))}
        >
          <DropdownMenuRadioItem key={0} value="0">
            <JLPTBadge level="*" />
            <span className="ml-2 italic">Show All</span>
          </DropdownMenuRadioItem>
          {Object.values(JLPT_LEVELS).map((level) => (
            <DropdownMenuRadioItem key={level} value={level.toString()}>
              <JLPTBadge level={level} />
              <span className="ml-2">{JLPT_LEVELS_DESCRIPTIONS[level]}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          disabled={!isSignedIn}
          checked={isShowKnownLemmasEnabled}
          onCheckedChange={toggleShowKnownLemmas}
        >
          Show Known Words
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
